import { showUserAlert } from "../utils/userErrorMessage";
// OperationStatus.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { supabase } from "../supabase/client";
import Header from "../Component/Header";
import { clampDateString, normalizeDateRange, validateNativeDateInputs } from "../utils/dateUtils";

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getCurrentMonthRange = () => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return {
    from: formatDate(firstDay),
    to: formatDate(lastDay),
  };
};

const formatKoreanDate = (value) => {
  if (!value) return "";
  const [, month, day] = String(value).split("-");
  return `${Number(month)}월 ${Number(day)}일`;
};

export default function OperationStatus() {
  const [filters, setFilters] = useState({ 사용자: "", 차량번호: "", 기사명: "" });
  const [dateRange, setDateRange] = useState(getCurrentMonthRange);
  const [printedCriteria, setPrintedCriteria] = useState({
    filters: { 사용자: "", 차량번호: "", 기사명: "" },
    dateRange: getCurrentMonthRange(),
  });
  const [data, setData] = useState([]);
  const [total, setTotal] = useState({ money: 0, driver_money: 0 });
  const [type, setType] = useState("전체");
  const [printMode, setPrintMode] = useState(null); // 'boss' | 'driver' | null
  const [driverPreviewOpen, setDriverPreviewOpen] = useState(false);
  const driverPreviewRef = useRef(null);
  const fromDateInputRef = useRef(null);
  const toDateInputRef = useRef(null);

  const printNow = (mode /* 'boss' | 'driver' */) => {
    setPrintMode(mode);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => window.print());
    });
  };

  const openDriverPreview = () => {
    setPrintedCriteria({
      filters: { ...filters },
      dateRange: { ...dateRange },
    });
    setDriverPreviewOpen(true);
  };

  useEffect(() => {
    const reset = () => setPrintMode(null);
    window.addEventListener("afterprint", reset);
    return () => window.removeEventListener("afterprint", reset);
  }, []);

  // ================== 데이터 조회 (페이지네이션) ==================
  const fetchData = async (currentFilters = filters, currentDateRange = dateRange) => {
    const PAGE_SIZE = 1000; // 한 번에 가져올 행 수
    const companyId = localStorage.getItem("email");
    setPrintedCriteria({
      filters: { ...currentFilters },
      dateRange: { ...currentDateRange },
    });

    if (!companyId) {
      setData([]);
      setTotal({ money: 0, driver_money: 0 });
      return;
    }

    // 매번 새로운 쿼리 빌더를 만들어주는 함수
    const buildBaseQuery = () => {
      let query = supabase
        .from("reservations")
        .select("*")
        .eq("company", companyId);

      if (currentDateRange.from) query = query.gte("date", currentDateRange.from);
      if (currentDateRange.to) query = query.lte("date", currentDateRange.to);
      if (type !== "전체") query = query.eq("reservation_type", type);

      const { 사용자, 차량번호, 기사명 } = currentFilters;
      if (사용자) query = query.ilike("user", `%${사용자}%`);
      if (차량번호) query = query.ilike("car_number", `%${차량번호}%`);
      if (기사명) query = query.ilike("human_name", `%${기사명}%`);

      // 정렬 (운행일 → id)
      query = query.order("date", { ascending: true }).order("id", { ascending: true });

      return query;
    };

    let page = 0;
    let allRows = [];

    while (true) {
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const { data: chunk, error } = await buildBaseQuery().range(from, to);

      if (error) {
        console.error("Supabase 데이터 오류:", error.message);
        setData([]);
        setTotal({ money: 0, driver_money: 0 });
        return;
      }

      if (!chunk || chunk.length === 0) break;

      allRows = allRows.concat(chunk);

      // 마지막 페이지면 종료
      if (chunk.length < PAGE_SIZE) break;

      page += 1;
    }

    // 차량번호 → 날짜 순 정렬 (혹시나 DB에서 순서가 섞였을 경우를 위해)
    const displaySorted = allRows.sort((a, b) => {
      const carCmp = (a.car_number || "").localeCompare(b.car_number || "");
      if (carCmp !== 0) return carCmp;
      return new Date(a.date) - new Date(b.date);
    });

    const moneySum = displaySorted.reduce((acc, cur) => acc + (cur.money || 0), 0);
    const driverMoneySum = displaySorted.reduce((acc, cur) => acc + (cur.driver_money || 0), 0);

    setData(displaySorted);
    setTotal({ money: moneySum, driver_money: driverMoneySum });
  };


  const handleFilter = () => {
    if (!validateNativeDateInputs([fromDateInputRef.current, toDateInputRef.current])) return;
    const nextDateRange = normalizeDateRange(dateRange);
    setDateRange(nextDateRange);
    fetchData(filters, nextDateRange);
  };

  const groupByCarNumber = (rows) => {
    const grouped = {};
    for (const row of rows) {
      const car = row.car_number || "미입력";
      if (!grouped[car]) grouped[car] = [];
      grouped[car].push(row);
    }
    return grouped;
  };

  const handleSave = async () => {
    const company = localStorage.getItem("email") || "";
    for (const row of data) {
      const { id, money, driver_money } = row;
      const { error } = await supabase
        .from("reservations")
        .update({ money, driver_money })
        .eq("id", id)
        .eq("company", company);
      if (error) console.error(`업데이트 오류 (ID: ${id})`, error);
    }
    showUserAlert("저장되었습니다.");
    fetchData();
  };

  const printTitle = useMemo(() => {
    const criteriaFilters = printedCriteria.filters || {};
    const criteriaDateRange = printedCriteria.dateRange || dateRange;
    const target =
      criteriaFilters.차량번호 ||
      criteriaFilters.기사명 ||
      criteriaFilters.사용자 ||
      (type !== "전체" ? type : "전체");
    const from = formatKoreanDate(criteriaDateRange.from);
    const to = formatKoreanDate(criteriaDateRange.to);

    if (from && to) return `${target}의 ${from}~${to}의 운행현황표`;
    return `${target}의 운행현황표`;
  }, [printedCriteria, dateRange, type]);

  const dateSequenceByRowId = useMemo(() => {
    const carStateMap = new Map();

    data.forEach((row) => {
      const carKey = row.car_number || "미입력";
      const dateKey = row.date || "";
      if (!carStateMap.has(carKey)) {
        carStateMap.set(carKey, {
          firstRowIdByDate: new Map(),
          sequenceByDate: new Map(),
          sequence: 0,
        });
      }

      const carState = carStateMap.get(carKey);
      if (!carState.sequenceByDate.has(dateKey)) {
        carState.sequence += 1;
        carState.sequenceByDate.set(dateKey, carState.sequence);
        carState.firstRowIdByDate.set(dateKey, row.id);
      }
    });

    const result = new Map();
    data.forEach((row) => {
      const carKey = row.car_number || "미입력";
      const dateKey = row.date || "";
      const carState = carStateMap.get(carKey);
      const isFirstRowForDate = carState?.firstRowIdByDate.get(dateKey) === row.id;
      result.set(row.id, isFirstRowForDate ? carState.sequenceByDate.get(dateKey) : "");
    });

    return result;
  }, [data]);

  const formatTime = (value) =>
    value
      ? new Date(`1970-01-01T${value}`).toLocaleTimeString("ko-KR", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : "";

  const driverPrintColumns = [
    { key: "seq", label: "순번", width: 70, align: "center" },
    { key: "reservation_type", label: "예약타입", width: 110, align: "center" },
    { key: "one_round", label: "구분", width: 80, align: "center" },
    { key: "date", label: "운행일자", width: 150, align: "center" },
    { key: "user", label: "사용자", width: 360, align: "left" },
    { key: "time", label: "출발시간", width: 110, align: "center" },
    { key: "driver_money", label: "기사금액", width: 150, align: "right" },
  ];

  const getDriverPrintRows = () => {
    const rows = [];
    Object.entries(groupByCarNumber(data)).forEach(([carNumber, carRows]) => {
      carRows.forEach((row) => {
        rows.push({
          id: row.id,
          seq: dateSequenceByRowId.get(row.id) || "",
          reservation_type: row.reservation_type || "",
          one_round: row.one_round || "",
          date: row.date || "",
          user: row.user || "",
          time: formatTime(row.time),
          driver_money: row.driver_money ? row.driver_money.toLocaleString() : "",
        });
      });
      const carDriverMoneySum = carRows.reduce((sum, row) => sum + (row.driver_money || 0), 0);
      rows.push({
        id: `subtotal-${carNumber}`,
        subtotal: true,
        label: `${carNumber} 차량 소계`,
        driver_money: carDriverMoneySum.toLocaleString(),
      });
    });
    return rows;
  };

  const drawWrappedText = (ctx, text, x, y, maxWidth, lineHeight, maxLines = 2) => {
    const words = String(text || "").split(/\s+/).filter(Boolean);
    const lines = [];
    let line = "";

    if (words.length === 0 && text) words.push(String(text));

    words.forEach((word) => {
      const testLine = line ? `${line} ${word}` : word;
      if (ctx.measureText(testLine).width <= maxWidth || !line) {
        line = testLine;
      } else {
        lines.push(line);
        line = word;
      }
    });
    if (line) lines.push(line);

    const visible = lines.slice(0, maxLines);
    visible.forEach((lineText, index) => {
      const suffix = index === maxLines - 1 && lines.length > maxLines ? "..." : "";
      ctx.fillText(`${lineText}${suffix}`, x, y + index * lineHeight);
    });
  };

  const downloadDriverImage = async () => {
    const rows = getDriverPrintRows();
    if (rows.length === 0) {
      showUserAlert("다운로드할 운행 내역이 없습니다.");
      return;
    }

    const preferredScale = 2;
    const padding = 48;
    const titleHeight = 84;
    const headerHeight = 42;
    const rowHeight = 38;
    const tableWidth = driverPrintColumns.reduce((sum, col) => sum + col.width, 0);
    const width = tableWidth + padding * 2;
    const height = padding * 2 + titleHeight + headerHeight + rowHeight * rows.length;
    const maxCanvasSide = 30000;
    const scale = Math.max(
      1,
      Math.min(preferredScale, maxCanvasSide / width, maxCanvasSide / height)
    );

    const canvas = document.createElement("canvas");
    canvas.width = Math.floor(width * scale);
    canvas.height = Math.floor(height * scale);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext("2d");
    ctx.scale(scale, scale);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);

    ctx.fillStyle = "#111827";
    ctx.font = "700 28px Arial, sans-serif";
    ctx.textBaseline = "middle";
    ctx.fillText(printTitle, padding, padding + 22);
    ctx.font = "14px Arial, sans-serif";
    ctx.fillStyle = "#4b5563";
    ctx.fillText(`생성일: ${formatDate(new Date())}`, padding, padding + 54);

    let y = padding + titleHeight;
    let x = padding;
    ctx.font = "700 17px Arial, sans-serif";
    driverPrintColumns.forEach((col) => {
      ctx.fillStyle = "#2563eb";
      ctx.fillRect(x, y, col.width, headerHeight);
      ctx.strokeStyle = "#1d4ed8";
      ctx.strokeRect(x, y, col.width, headerHeight);
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.fillText(col.label, x + col.width / 2, y + headerHeight / 2);
      x += col.width;
    });

    y += headerHeight;
    ctx.font = "15px Arial, sans-serif";
    rows.forEach((row) => {
      x = padding;
      const isSubtotal = row.subtotal;
      ctx.fillStyle = isSubtotal ? "#fef3c7" : "#ffffff";
      ctx.fillRect(x, y, tableWidth, rowHeight);

      if (isSubtotal) {
        ctx.strokeStyle = "#d1d5db";
        ctx.strokeRect(x, y, tableWidth - driverPrintColumns.at(-1).width, rowHeight);
        ctx.fillStyle = "#111827";
        ctx.textAlign = "right";
        ctx.font = "700 15px Arial, sans-serif";
        ctx.fillText(row.label, x + tableWidth - driverPrintColumns.at(-1).width - 10, y + rowHeight / 2);
        x += tableWidth - driverPrintColumns.at(-1).width;
        ctx.strokeRect(x, y, driverPrintColumns.at(-1).width, rowHeight);
        ctx.textAlign = "right";
        ctx.fillText(row.driver_money, x + driverPrintColumns.at(-1).width - 10, y + rowHeight / 2);
        ctx.font = "15px Arial, sans-serif";
        y += rowHeight;
        return;
      }

      driverPrintColumns.forEach((col) => {
        ctx.strokeStyle = "#d1d5db";
        ctx.strokeRect(x, y, col.width, rowHeight);
        ctx.fillStyle = "#111827";
        ctx.textAlign = col.align === "right" ? "right" : col.align === "left" ? "left" : "center";
        const textX =
          col.align === "right" ? x + col.width - 10 : col.align === "left" ? x + 10 : x + col.width / 2;
        if (col.key === "user") {
          ctx.textBaseline = "top";
          drawWrappedText(ctx, row[col.key], textX, y + 6, col.width - 20, 16, 2);
          ctx.textBaseline = "middle";
        } else {
          ctx.fillText(row[col.key] || "", textX, y + rowHeight / 2);
        }
        x += col.width;
      });
      y += rowHeight;
    });

    const link = document.createElement("a");
    const safeTitle = printTitle.replace(/[\\/:*?"<>|]/g, "_");
    link.download = `${safeTitle}.png`;
    canvas.toBlob((blob) => {
      if (!blob || blob.size === 0) {
        showUserAlert("이미지 파일을 만들지 못했습니다. 조회된 행이 너무 많으면 검색 범위를 줄여 다시 시도해 주세요.");
        return;
      }

      const url = URL.createObjectURL(blob);
      link.href = url;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, "image/png");
  };

  return (
    <div className={`space-y-1 ${printMode === 'driver' ? 'print-driver' : ''} ${printMode === 'boss' ? 'print-boss' : ''}`}>
      {/* 프린트 전용 스타일 */}
      <style>{`
        .operation-status-table th,
        .operation-status-table td {
          padding-top: 0.125rem !important;
          padding-bottom: 0.125rem !important;
          line-height: 1.15 !important;
          text-align: center;
        }

        .operation-status-table input {
          min-height: 1.5rem !important;
          padding-top: 0.125rem !important;
          padding-bottom: 0.125rem !important;
          line-height: 1.15 !important;
        }

        .operation-status-table .col-seq {
          width: 36px;
          min-width: 36px;
          white-space: nowrap;
        }

        .operation-status-table .col-reservation-type {
          width: 52px;
          min-width: 52px;
          white-space: nowrap;
        }

        .operation-status-table .col-date {
          width: 96px;
          min-width: 96px;
          white-space: nowrap;
        }

        .operation-status-table .col-car {
          width: 86px;
          min-width: 86px;
          white-space: nowrap;
        }

        .operation-status-table .col-driver {
          width: 56px;
          min-width: 56px;
          white-space: nowrap;
          word-break: keep-all;
        }

        .operation-status-table .col-time {
          width: 54px;
          min-width: 54px;
          white-space: nowrap;
        }

        .operation-status-table .col-trip-type {
          width: 44px;
          min-width: 44px;
          white-space: nowrap;
        }

        .operation-status-table .col-start,
        .operation-status-table .col-destination {
          width: 160px;
          min-width: 160px;
          text-align: left;
        }

        .operation-status-table .col-user {
          text-align: left;
        }

        .operation-status-table .col-money,
        .operation-status-table .col-driver-money {
          width: 112px;
          min-width: 112px;
          white-space: nowrap;
          text-align: right;
        }

        .operation-status-table .money-input {
          width: 104px;
          text-align: right;
        }

        .operation-status-table .print-money-text {
          display: none;
        }

        @media print {
          @page { size: A4 landscape; margin: 10mm; }
          @page driver-print { size: A4 portrait; margin: 10mm; }
          .print-driver { page: driver-print; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }

          .operation-status-table {
            font-size: 11px !important;
          }

          .operation-status-table th,
          .operation-status-table td {
            padding-top: 1px !important;
            padding-bottom: 1px !important;
            line-height: 1.1 !important;
          }

          .operation-status-table input {
            min-height: 18px !important;
            padding-top: 1px !important;
            padding-bottom: 1px !important;
            line-height: 1.1 !important;
          }

          .print-boss th.col-money,
          .print-boss td.col-money { display: table-cell !important; }

          .print-boss .operation-status-table {
            width: 100% !important;
            table-layout: fixed !important;
          }

          .print-boss .operation-status-table .col-seq {
            width: 30px !important;
            min-width: 30px !important;
          }

          .print-boss .operation-status-table .col-reservation-type,
          .print-boss .operation-status-table .col-trip-type {
            width: 42px !important;
            min-width: 42px !important;
          }

          .print-boss .operation-status-table .col-date {
            width: 78px !important;
            min-width: 78px !important;
          }

          .print-boss .operation-status-table .col-car {
            width: 62px !important;
            min-width: 62px !important;
          }

          .print-boss .operation-status-table .col-driver {
            width: 54px !important;
            min-width: 54px !important;
          }

          .print-boss .operation-status-table .col-time {
            width: 44px !important;
            min-width: 44px !important;
          }

          .print-boss .operation-status-table .col-money {
            width: 86px !important;
            min-width: 86px !important;
          }

          .print-boss .operation-status-table .col-driver-money {
            width: 78px !important;
            min-width: 78px !important;
          }

          .print-boss .operation-status-table .col-user,
          .print-boss .operation-status-table .col-start,
          .print-boss .operation-status-table .col-destination {
            width: calc((100% - 516px) / 3) !important;
            min-width: 0 !important;
          }

          .print-boss .operation-status-table .money-input {
            display: none !important;
          }

          .print-boss .operation-status-table .print-money-text {
            display: inline !important;
          }

          .print-driver th.col-money,
          .print-driver td.col-money { display: none !important; }

          .print-driver th.col-car,
          .print-driver td.col-car,
          .print-driver th.col-driver,
          .print-driver td.col-driver,
          .print-driver th.col-start,
          .print-driver td.col-start,
          .print-driver th.col-destination,
          .print-driver td.col-destination {
            display: none !important;
          }

          /* 기사명: 가로 고정 + 줄바꿈 금지 */
          th.col-driver, td.col-driver {
            white-space: nowrap !important;
            word-break: keep-all !important;
            writing-mode: horizontal-tb !important;
            text-orientation: mixed !important;
            min-width: 60px;
          }
        }
      `}</style>

      <Header />

      <div className="px-4 pt-1 pb-4 space-y-4">
        {/* 상단 액션 (화면 전용) */}
        <div className="flex items-center justify-between no-print">
          <h2 className="text-lg font-semibold">운행 현황표</h2>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={openDriverPreview}
            >
              기사용 인쇄
            </Button>
            <Button variant="outline" onClick={() => printNow('boss')}>
              사장용 인쇄
            </Button>
          </div>
        </div>

        {/* 프린트 전용 머리말 (공통) */}
        <div className="print-only mb-2">
          <div className="text-xl font-bold">{printTitle}</div>
        </div>

        <div>
          <div className="flex flex-wrap gap-2 no-print">
            <select value={type} onChange={(e) => setType(e.target.value)} className="w-32">
              <option>전체</option>
              <option>일반</option>
              <option>학단</option>
              <option>통근</option>
              <option>투어</option>
            </select>
            {Object.entries(filters).map(([key, value]) => (
              <Input
                key={key}
                placeholder={key}
                value={value}
                onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
                className="w-40"
              />
            ))}
          </div>

          <div className="flex flex-wrap gap-2 items-center mt-2 no-print">
            <span className="text-sm">운행일자:</span>
            <Input ref={fromDateInputRef} type="date" value={dateRange.from} onChange={(e) => setDateRange({ ...dateRange, from: clampDateString(e.target.value) })} />
            <span className="text-sm">~</span>
            <Input ref={toDateInputRef} type="date" value={dateRange.to} onChange={(e) => setDateRange({ ...dateRange, to: clampDateString(e.target.value) })} />
            <Button onClick={handleFilter}>검색</Button>
          </div>

          <Card>
            <CardContent className="bg-white">
              <div className="overflow-x-auto">
                <table className="operation-status-table table-auto w-full border border-gray-300 text-sm">
                  <thead className="bg-blue-500 text-white">
                    <tr>
                      {["순번", "예약타입", "구분", "운행일자", "차량번호", "기사명", "사용자", "출발시간", "출발지", "행선지", "받을 금액", "기사금액"].map((header) => (
                        <th
                          key={header}
                          className={
                            "border border-gray-300 p-2 " +
                            (header === "순번" ? "col-seq " : "") +
                            (header === "예약타입" ? "col-reservation-type " : "") +
                            (header === "받을 금액" ? "col-money " : "") +
                            (header === "기사금액" ? "col-driver-money " : "") +
                            (header === "구분" ? "col-trip-type " : "") +
                            (header === "운행일자" ? "col-date " : "") +
                            (header === "차량번호" ? "col-car " : "") +
                            (header === "기사명" ? "col-driver " : "") +
                            (header === "사용자" ? "col-user " : "") +
                            (header === "출발시간" ? "col-time " : "") +
                            (header === "출발지" ? "col-start " : "") +
                            (header === "행선지" ? "col-destination " : "")
                          }
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-gray-200 font-semibold">
                      <td className="border p-2 text-center">누계</td>
                      <td className="border p-2 text-center">-</td>
                      <td colSpan={8} className="border p-2 text-center">총 합계</td>
                      <td className="border p-2 col-money">{total.money.toLocaleString()}</td>
                      <td className="border p-2 col-driver-money">{total.driver_money.toLocaleString()}</td>
                    </tr>
                    {Object.entries(groupByCarNumber(data)).map(([carNumber, rows], groupIndex) => {
                      const carMoneySum = rows.reduce((sum, row) => sum + (row.money || 0), 0);
                      const carDriverMoneySum = rows.reduce((sum, row) => sum + (row.driver_money || 0), 0);

                      return (
                        <React.Fragment key={groupIndex}>
                          {rows.map((row) => (
                            <tr key={row.id}>
                              <td className="border border-gray-300 p-2 col-seq">{dateSequenceByRowId.get(row.id)}</td>
                              <td className="border border-gray-300 p-2 col-reservation-type">{row.reservation_type}</td>
                              <td className="border border-gray-300 p-2 col-trip-type">{row.one_round}</td>
                              <td className="border border-gray-300 p-2 col-date">{row.date}</td>
                              <td className="border border-gray-300 p-2 col-car">{row.car_number}</td>
                              <td className="border border-gray-300 p-2 col-driver">{row.human_name}</td>
                              <td className="border border-gray-300 p-2 col-user">{row.user}</td>
                              <td className="border border-gray-300 p-2 col-time">
                                {row.time
                                  ? new Date(`1970-01-01T${row.time}`).toLocaleTimeString("ko-KR", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: false,
                                    })
                                  : ""}
                              </td>
                              <td className="border border-gray-300 p-2 col-start">{row.start}</td>
                              <td className="border border-gray-300 p-2 col-destination">{row.destination}</td>
                              <td className="border border-gray-300 p-2 col-money">
                                <Input
                                  type="text"
                                  value={row.money || ""}
                                  onChange={(e) => {
                                    const idx = data.indexOf(row);
                                    const val = e.target.value;
                                    const updated = [...data];
                                    updated[idx].money = parseInt(val) || 0;
                                    setData(updated);
                                  }}
                                  className="money-input"
                                />
                                <span className="print-money-text">
                                  {(row.money || 0).toLocaleString()}
                                </span>
                              </td>
                              <td className="border border-gray-300 p-2 col-driver-money">
                                <Input
                                  type="text"
                                  value={row.driver_money || ""}
                                  onChange={(e) => {
                                    const idx = data.indexOf(row);
                                    const val = e.target.value;
                                    const updated = [...data];
                                    updated[idx].driver_money = parseInt(val) || 0;
                                    setData(updated);
                                  }}
                                  className="money-input"
                                />
                                <span className="print-money-text">
                                  {(row.driver_money || 0).toLocaleString()}
                                </span>
                              </td>
                            </tr>
                          ))}
                          {/* 차량별 소계 */}
                          <tr className="bg-yellow-100 font-semibold">
                            <td colSpan={10} className="border p-2 text-right">{carNumber} 차량 소계</td>
                            <td className="border p-2 col-money">{carMoneySum.toLocaleString()}</td>
                            <td className="border p-2 col-driver-money">{carDriverMoneySum.toLocaleString()}</td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="text-right mt-2 pr-4 no-print">
            <Button onClick={handleSave}>저장</Button>
          </div>
        </div>

        {driverPreviewOpen && (
          <div
            className="fixed inset-0 z-[220] flex items-center justify-center p-4 no-print"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.55)" }}
          >
            <div className="max-h-[92vh] w-full max-w-5xl overflow-hidden rounded bg-white shadow-xl">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <div className="font-semibold">기사용 인쇄 미리보기</div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => printNow("driver")}>
                    인쇄
                  </Button>
                  <Button variant="outline" onClick={downloadDriverImage}>
                    이미지 다운로드
                  </Button>
                  <Button variant="outline" onClick={() => setDriverPreviewOpen(false)}>
                    닫기
                  </Button>
                </div>
              </div>
              <div className="max-h-[calc(92vh-64px)] overflow-auto bg-gray-100 p-4">
                <div
                  ref={driverPreviewRef}
                  className="mx-auto bg-white p-5 text-sm shadow-sm"
                  style={{ width: "760px" }}
                >
                  <div className="mb-3">
                    <div className="text-xl font-bold">{printTitle}</div>
                    <div className="text-xs text-gray-500">이미지 다운로드 시 큰 PNG 파일로 저장됩니다.</div>
                  </div>
                  <table className="w-full table-auto border border-gray-300 text-sm">
                    <thead className="bg-blue-500 text-white">
                      <tr>
                        {driverPrintColumns.map((col) => (
                          <th key={col.key} className="border border-gray-300 px-2 py-1 text-center">
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {getDriverPrintRows().map((row) =>
                        row.subtotal ? (
                          <tr key={row.id} className="bg-yellow-100 font-semibold">
                            <td className="border border-gray-300 px-2 py-1 text-right" colSpan={driverPrintColumns.length - 1}>
                              {row.label}
                            </td>
                            <td className="border border-gray-300 px-2 py-1 text-right">{row.driver_money}</td>
                          </tr>
                        ) : (
                          <tr key={row.id}>
                            {driverPrintColumns.map((col) => (
                              <td
                                key={col.key}
                                className={
                                  "border border-gray-300 px-2 py-1 " +
                                  (col.align === "right" ? "text-right" : col.align === "left" ? "text-left" : "text-center")
                                }
                              >
                                {row[col.key]}
                              </td>
                            ))}
                          </tr>
                        )
                      )}
                      {data.length === 0 && (
                        <tr>
                          <td className="border border-gray-300 px-2 py-6 text-center text-gray-500" colSpan={driverPrintColumns.length}>
                            조회된 운행 내역이 없습니다.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
